import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { submissionHistory } from '../data/loan';
import { providerResults, providers } from '../data/providers';
import type { AusResult, HistoryEntry, ProviderId, RunMode, RunStatus, Step } from '../types/aus';
import { LoanSummaryBar } from '../components/aus/LoanSummaryBar';
import { ProcessingPanel } from '../components/aus/ProcessingPanel';
import { ProviderSelect } from '../components/aus/ProviderSelect';
import { ResultsScreen } from '../components/aus/ResultsScreen';
import { ReviewScreen } from '../components/aus/ReviewScreen';
import { StepIndicator } from '../components/aus/StepIndicator';
import { SubmissionHistory } from '../components/aus/SubmissionHistory';
import { AppHeader } from '../components/AppHeader';
const stepNumber: Record<Step, 1 | 2 | 3> = {
  select: 1,
  review: 2,
  processing: 2,
  results: 3
};
const idleStatuses: Record<ProviderId, RunStatus> = {
  fannie: 'pending',
  freddie: 'pending'
};
export function AutomatedUnderwriting() {
  const [step, setStep] = useState<Step>('select');
  const [mode, setMode] = useState<RunMode | null>(null);
  const [results, setResults] = useState<AusResult[]>([]);
  const [statuses, setStatuses] = useState<Record<ProviderId, RunStatus>>(idleStatuses);
  const [history, setHistory] = useState<HistoryEntry[]>(submissionHistory);
  const [creditReady, setCreditReady] = useState(true);
  const [credentialsReady, setCredentialsReady] = useState(true);
  const timers = useRef<number[]>([]);
  useEffect(() => () => {
    timers.current.forEach(id => window.clearTimeout(id));
  }, []);
  const startReview = (next: RunMode) => {
    setMode(next);
    setStep('review');
  };
  const recordRun = (provider: ProviderId, dual: boolean) => {
    const result = providerResults[provider];
    setResults(current => [...current, result]);
    setHistory(current => [{
      id: `run-${provider}-${Date.now()}`,
      provider,
      result: result.recommendation,
      submittedAt: result.submittedAt,
      submittedBy: 'Stephen Western',
      dual
    }, ...current]);
  };
  const submit = () => {
    if (!mode || step === 'processing') return;
    setResults([]);
    setStep('processing');
    timers.current.forEach(id => window.clearTimeout(id));
    timers.current = [];
    if (mode === 'dual') {
      setStatuses({
        fannie: 'running',
        freddie: 'pending'
      });
      timers.current.push(window.setTimeout(() => {
        recordRun('fannie', true);
        setStatuses({
          fannie: 'complete',
          freddie: 'running'
        });
      }, 2200));
      timers.current.push(window.setTimeout(() => {
        recordRun('freddie', true);
        setStatuses({
          fannie: 'complete',
          freddie: 'complete'
        });
        setStep('results');
      }, 4400));
      return;
    }
    setStatuses({
      ...idleStatuses,
      [mode]: 'running'
    });
    timers.current.push(window.setTimeout(() => {
      recordRun(mode, false);
      setStatuses({
        ...idleStatuses,
        [mode]: 'complete'
      });
      setStep('results');
    }, 2400));
  };
  const viewHistoryEntry = (entry: HistoryEntry) => {
    setMode(entry.provider);
    setResults([{
      ...providerResults[entry.provider],
      submittedAt: entry.submittedAt
    }]);
    setStep('results');
  };
  const lastProvider = history[0]?.provider ?? null;
  return <div className="min-h-full w-full bg-slate-50 pb-10" data-id="element-334" data-mp="25a1rg2864mn4">
      <AppHeader data-id="element-335" data-mp="2a94y1r3sr0x7" />
      <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6" data-id="element-336" data-mp="2a7k6898v9w31">
        <header className="mb-6" data-id="element-337" data-mp="1w93xkcrg1vi9">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-graphite-800" data-id="element-338" data-mp="1w82atb12fkkp">
            Automated Underwriting
          </h1>
          <p className="mt-1 text-sm text-slate-500" data-id="element-339" data-mp="20c9z573m2yie">
            Run underwriting findings for this loan using Fannie Mae or Freddie Mac.
          </p>
        </header>

        <div className="mb-6 space-y-4" data-id="element-340" data-mp="owtp27mhd4ss">
          <LoanSummaryBar data-id="element-341" data-mp="15252y3280aza" />
          <StepIndicator current={stepNumber[step]} data-id="element-342" data-mp="153ghmxx7f356" />
        </div>

        <AnimatePresence mode="wait" data-id="element-343" data-mp="10bygo9v6l2k7">
          <motion.div key={step + (mode ?? '')} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-344" data-mp="10awsd3p5mjkj">
            {step === 'select' && <div className="space-y-4" data-id="element-345" data-mp="1o07twnebwhzq">
                <ProviderSelect selected={mode} onSelect={setMode} onContinue={startReview} lastProvider={lastProvider} data-id="element-346" data-mp="1nywv71zqq0fh" />
                <SubmissionHistory entries={history} onSelect={viewHistoryEntry} data-id="element-347" data-mp="1a1hyrdb2kse0" />
                <div className="flex flex-wrap items-center gap-3 pt-2 text-xs text-slate-400" data-id="element-348" data-mp="1a3kr43zp2xlw">
                  <span data-id="element-349" data-mp="2ltr08sqo8lb">Prototype states:</span>
                  <button type="button" onClick={() => setCreditReady(value => !value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 font-medium text-slate-500 transition-colors hover:text-slate-800" data-id="element-350" data-mp="3urh3ldwtilce">
                    Credit report {creditReady ? 'found' : 'missing'}
                  </button>
                  <button type="button" onClick={() => setCredentialsReady(value => !value)} className="rounded-md border border-slate-200 bg-white px-2 py-1 font-medium text-slate-500 transition-colors hover:text-slate-800" data-id="element-351" data-mp="2o1cpoccxthvd">
                    Credentials {credentialsReady ? 'connected' : 'missing'}
                  </button>
                </div>
              </div>}

            {step === 'review' && mode && <ReviewScreen mode={mode} creditReady={creditReady} credentialsReady={credentialsReady} onResolveCredit={() => setCreditReady(true)} onResolveCredentials={() => setCredentialsReady(true)} onBack={() => setStep('select')} onSubmit={submit} data-id="element-352" data-mp="17nwccveot4sh" />}

            {step === 'processing' && mode && <ProcessingPanel mode={mode} statuses={statuses} data-id="element-353" data-mp="2ov5hvf6eepuh" />}

            {step === 'results' && results.length > 0 && <ResultsScreen results={results} history={history} onRunAgain={() => setStep('review')} onSelectHistory={viewHistoryEntry} onDone={() => setStep('select')} data-id="element-354" data-mp="2yuwatcfb8wsg" />}
          </motion.div>
        </AnimatePresence>

        <p className="mt-8 text-center text-xs text-slate-400" data-id="element-355" data-mp="33mxxlsa3u5wq">
          {providers.fannie.shortLabel} and {providers.freddie.shortLabel} submissions use the
          credentials and institution settings saved for MortgagePros, LLC.
        </p>
      </main>
    </div>;
}